import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { GroupedProjectItems } from '../components/ProjectOptionLabel';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Receipt, Plus, Trash2, Edit2, Save, AlertTriangle, Check, DollarSign, Percent, Wallet, ClipboardList, Database, Layers } from 'lucide-react';
import { toast } from 'sonner';
import PettyCashAdminTab from '../components/PettyCashAdminTab';
import ChangeOrdersTab from '../components/ChangeOrdersTab';
import AccountingDatabaseTab from '../components/AccountingDatabaseTab';
import ProjectBOMTab from '../components/ProjectBOMTab';

const API_URL = '';

const VALID_STATUSES = [
  "For Billing", "Billed", "On-hold", "Terminated", 
  "For deposit", "Deposited", "Alloted"
];

const STATUS_COLORS = {
  "For Billing": "bg-yellow-500/20 text-yellow-600 border-yellow-500/40",
  "Billed": "bg-blue-500/20 text-blue-600 border-blue-500/40",
  "On-hold": "bg-orange-500/20 text-orange-600 border-orange-500/40",
  "Terminated": "bg-red-500/20 text-red-600 border-red-500/40",
  "For deposit": "bg-purple-500/20 text-purple-600 border-purple-500/40",
  "Deposited": "bg-green-500/20 text-green-600 border-green-500/40",
  "Alloted": "bg-teal-500/20 text-teal-600 border-teal-500/40",
};

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-PH', { 
    style: 'currency', 
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2 
  }).format(amount);
}

function BillingPhaseRow({ phase, category, contractPrice, showAccomplishment, onUpdate, onDelete }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ ...phase });
  
  const amount = (contractPrice * phase.percentage) / 100;
  const readyToBill = showAccomplishment && (phase.percentage > 0) && ((phase.accomplishment ?? 0) >= phase.percentage) && phase.status === 'For Billing';
  
  const handleSave = async () => {
    await onUpdate(phase.id, editData);
    setIsEditing(false);
  };
  
  const handleStatusChange = async (newStatus) => {
    await onUpdate(phase.id, { ...phase, status: newStatus });
  };
  
  const handleUrgentToggle = async (checked) => {
    await onUpdate(phase.id, { ...phase, is_urgent: checked });
  };
  
  const handlePaidDateChange = async (date) => {
    await onUpdate(phase.id, { ...phase, paid_date: date });
  };
  
  return (
    <TableRow className={phase.is_urgent ? 'bg-red-500/5' : (readyToBill ? 'bg-green-500/10' : '')}>
      <TableCell>
        {isEditing ? (
          <Input
            value={editData.name}
            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
            className="w-full"
          />
        ) : (
          <div className="flex items-center gap-2">
            {phase.is_urgent && <AlertTriangle className="w-4 h-4 text-red-500" />}
            <span className="font-medium">{phase.name}</span>
            {readyToBill && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-700 border border-green-500/40 font-semibold whitespace-nowrap" data-testid={`ready-to-bill-${phase.id}`}>
                Ready to Bill
              </span>
            )}
          </div>
        )}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <div className="flex items-center gap-1">
            <Input
              type="number"
              value={editData.percentage}
              onChange={(e) => setEditData({ ...editData, percentage: parseFloat(e.target.value) || 0 })}
              className="w-20"
              step="0.1"
            />
            <span>%</span>
          </div>
        ) : (
          <span>{phase.percentage}%</span>
        )}
      </TableCell>
      {showAccomplishment && (
        <TableCell>
          <div className="flex items-center gap-1">
            <Input
              type="number"
              value={phase.accomplishment ?? ''}
              onChange={(e) => onUpdate(phase.id, { accomplishment: e.target.value === '' ? null : (parseFloat(e.target.value) || 0) })}
              className="w-20"
              step="0.1"
              min="0"
              max="100"
              placeholder="0"
              data-testid={`phase-accomplishment-${phase.id}`}
            />
            <span>%</span>
          </div>
        </TableCell>
      )}
      <TableCell className="font-mono text-right">
        {formatCurrency(amount)}
      </TableCell>
      <TableCell>
        <Select value={phase.status} onValueChange={handleStatusChange}>
          <SelectTrigger className={`w-36 ${STATUS_COLORS[phase.status] || ''}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VALID_STATUSES.map(status => (
              <SelectItem key={status} value={status}>{status}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <Input
          type="date"
          value={phase.paid_date || ''}
          onChange={(e) => handlePaidDateChange(e.target.value || null)}
          className="w-36"
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Switch
            checked={phase.is_urgent}
            onCheckedChange={handleUrgentToggle}
          />
          <span className={`text-xs ${phase.is_urgent ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}>
            {phase.is_urgent ? 'URGENT' : 'Normal'}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          {isEditing ? (
            <>
              <Button size="sm" variant="ghost" onClick={handleSave}>
                <Check className="w-4 h-4 text-green-500" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setIsEditing(false); setEditData({ ...phase }); }}>
                <span className="text-xs">Cancel</span>
              </Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)}>
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onDelete(phase.id)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

function BillingCategory({ 
  title, 
  category, 
  phases, 
  contractPrice, 
  contractCode,
  startDate,
  targetCompletion,
  approvedBudget,
  status,
  scheduleProgress,
  onFieldChange,
  onPriceChange, 
  onPhaseUpdate, 
  onPhaseAdd, 
  onPhaseDelete 
}) {
  const [newPhaseName, setNewPhaseName] = useState('');
  const [newPhasePercent, setNewPhasePercent] = useState(0);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const showAccomplishment = category === 'construction';
  
  const totalPercentage = phases.reduce((sum, p) => sum + (p.percentage || 0), 0);
  const totalAmount = (contractPrice * totalPercentage) / 100;
  const isValidTotal = Math.abs(totalPercentage - 100) < 0.01;
  
  const handleAddPhase = () => {
    if (!newPhaseName.trim()) {
      toast.error('Please enter a phase name');
      return;
    }
    onPhaseAdd(category, newPhaseName, newPhasePercent);
    setNewPhaseName('');
    setNewPhasePercent(0);
    setShowAddDialog(false);
  };
  
  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              {category === 'design' ? <Percent className="w-5 h-5 text-blue-500" /> : <DollarSign className="w-5 h-5 text-green-500" />}
              {title}
              {showAccomplishment && scheduleProgress != null && (
                <span
                  className="text-sm font-semibold text-green-600 bg-green-500/10 border border-green-500/30 rounded-full px-2.5 py-0.5"
                  data-testid="construction-schedule-progress"
                  title="Overall accomplishment from the project's Construction Schedule"
                >
                  {scheduleProgress}% Accomplished
                </span>
              )}
            </CardTitle>
            {contractCode ? (
              <p className="text-xs text-muted-foreground mt-1" data-testid={`contract-code-${category}`}>
                Contract Code: <span className="font-mono font-semibold text-foreground">{contractCode}</span>
              </p>
            ) : (
              <p className="text-xs text-muted-foreground/70 mt-1">
                Contract Code auto-generates once a contract amount is set
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground">Total Contract Price:</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₱</span>
                <Input
                  type="number"
                  value={contractPrice || ''}
                  onChange={(e) => onPriceChange(parseFloat(e.target.value) || 0)}
                  className="w-48 pl-8 font-mono"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Manually-encoded schedule & budget */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5 p-3 rounded-lg bg-muted/30 border border-border/40">
          <div>
            <Label className="text-xs text-muted-foreground">Start Date</Label>
            <Input
              type="date"
              value={startDate || ''}
              onChange={(e) => onFieldChange('start_date', e.target.value || null)}
              className="mt-1"
              data-testid={`${category}-start-date`}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Target Completion</Label>
            <Input
              type="date"
              value={targetCompletion || ''}
              onChange={(e) => onFieldChange('target_completion', e.target.value || null)}
              className="mt-1"
              data-testid={`${category}-target-completion`}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Approved Budget</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₱</span>
              <Input
                type="number"
                value={approvedBudget || ''}
                onChange={(e) => onFieldChange('approved_budget', parseFloat(e.target.value) || 0)}
                className="pl-8 font-mono"
                placeholder="0.00"
                data-testid={`${category}-approved-budget`}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Status</Label>
            <Select value={status || 'Active'} onValueChange={(v) => onFieldChange('status', v)}>
              <SelectTrigger className="mt-1" data-testid={`${category}-status`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Phase Name</TableHead>
              <TableHead className="w-[100px]">%</TableHead>
              {showAccomplishment && <TableHead className="w-[140px]">Accomplishment</TableHead>}
              <TableHead className="w-[150px] text-right">Amount</TableHead>
              <TableHead className="w-[150px]">Status</TableHead>
              <TableHead className="w-[150px]">Paid Date</TableHead>
              <TableHead className="w-[120px]">Urgent</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {phases.map(phase => (
              <BillingPhaseRow
                key={phase.id}
                phase={phase}
                category={category}
                contractPrice={contractPrice}
                showAccomplishment={showAccomplishment}
                onUpdate={(phaseId, data) => onPhaseUpdate(category, phaseId, data)}
                onDelete={(phaseId) => onPhaseDelete(category, phaseId)}
              />
            ))}
          </TableBody>
        </Table>
        
        {/* Summary Row */}
        <div className={`mt-4 p-3 rounded-lg flex items-center justify-between ${isValidTotal ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
          <div className="flex items-center gap-2">
            {isValidTotal ? (
              <Check className="w-5 h-5 text-green-500" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-500" />
            )}
            <span className={`font-medium ${isValidTotal ? 'text-green-600' : 'text-red-600'}`}>
              Total: {totalPercentage.toFixed(1)}%
              {!isValidTotal && ` (should be 100%)`}
            </span>
          </div>
          <span className="font-mono font-bold text-lg">
            {formatCurrency(totalAmount)}
          </span>
        </div>
        
        {/* Add Phase Button */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button variant="outline" className="mt-4" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Phase
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Phase</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Phase Name</Label>
                <Input
                  value={newPhaseName}
                  onChange={(e) => setNewPhaseName(e.target.value)}
                  placeholder="e.g., Final Payment"
                />
              </div>
              <div>
                <Label>Percentage (%)</Label>
                <Input
                  type="number"
                  value={newPhasePercent}
                  onChange={(e) => setNewPhasePercent(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  step="0.1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button onClick={handleAddPhase}>Add Phase</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function ChangeOrderBillingRow({ co, onUpdate }) {
  const [budget, setBudget] = useState(co.co_approved_budget || '');
  return (
    <TableRow className={co.billing_is_urgent ? 'bg-red-500/5' : ''} data-testid={`co-billing-row-${co.id}`}>
      <TableCell>
        <div className="flex items-center gap-2">
          {co.billing_is_urgent && <AlertTriangle className="w-4 h-4 text-red-500" />}
          <div>
            <div className="font-medium">{co.control_number}</div>
            <div className="text-xs text-muted-foreground">{co.date_of_request || ''}</div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="relative w-36">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">₱</span>
          <Input
            type="number"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            onBlur={() => onUpdate(co.id, { co_approved_budget: parseFloat(budget) || 0 })}
            className="pl-6 font-mono"
            placeholder="0.00"
            data-testid={`co-approved-budget-${co.id}`}
          />
        </div>
      </TableCell>
      <TableCell>
        <Input
          type="date"
          value={co.co_start_date || ''}
          onChange={(e) => onUpdate(co.id, { co_start_date: e.target.value || null })}
          className="w-36"
          data-testid={`co-start-date-${co.id}`}
        />
      </TableCell>
      <TableCell>
        <Input
          type="date"
          value={co.co_target_completion || ''}
          onChange={(e) => onUpdate(co.id, { co_target_completion: e.target.value || null })}
          className="w-36"
          data-testid={`co-target-completion-${co.id}`}
        />
      </TableCell>
      <TableCell>
        <Select value={co.co_status || 'Active'} onValueChange={(v) => onUpdate(co.id, { co_status: v })}>
          <SelectTrigger className="w-32" data-testid={`co-status-${co.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="font-mono text-right">
        {formatCurrency(co.total_amount || 0)}
      </TableCell>
      <TableCell>
        <Select value={co.billing_status || 'For Billing'} onValueChange={(v) => onUpdate(co.id, { billing_status: v })}>
          <SelectTrigger className={`w-36 ${STATUS_COLORS[co.billing_status] || ''}`} data-testid={`co-billing-status-${co.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VALID_STATUSES.map(status => (
              <SelectItem key={status} value={status}>{status}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <Input
          type="date"
          value={co.billing_paid_date || ''}
          onChange={(e) => onUpdate(co.id, { billing_paid_date: e.target.value || null })}
          className="w-36"
          data-testid={`co-billing-paid-date-${co.id}`}
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Switch
            checked={!!co.billing_is_urgent}
            onCheckedChange={(checked) => onUpdate(co.id, { billing_is_urgent: checked })}
            data-testid={`co-billing-urgent-${co.id}`}
          />
          <span className={`text-xs ${co.billing_is_urgent ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}>
            {co.billing_is_urgent ? 'URGENT' : 'Normal'}
          </span>
        </div>
      </TableCell>
    </TableRow>
  );
}

function ChangeOrderBillingCategory({ changeOrders, onUpdate }) {
  const total = changeOrders.reduce((sum, co) => sum + (co.total_amount || 0), 0);
  return (
    <Card className="mb-6" data-testid="change-order-billing-category">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-purple-500" />
          Change Orders
        </CardTitle>
      </CardHeader>
      <CardContent>
        {changeOrders.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            No change orders for this project yet.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Change Order</TableHead>
                    <TableHead className="w-[150px]">Approved Budget</TableHead>
                    <TableHead className="w-[150px]">Start Date</TableHead>
                    <TableHead className="w-[150px]">Target Completion</TableHead>
                    <TableHead className="w-[130px]">Status</TableHead>
                    <TableHead className="w-[140px] text-right">Amount</TableHead>
                    <TableHead className="w-[150px]">Billing Status</TableHead>
                    <TableHead className="w-[150px]">Paid Date</TableHead>
                    <TableHead className="w-[120px]">Urgent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {changeOrders.map(co => (
                    <ChangeOrderBillingRow key={co.id} co={co} onUpdate={onUpdate} />
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-4 p-3 rounded-lg flex items-center justify-between bg-purple-500/10 border border-purple-500/30">
              <span className="font-medium text-purple-600">
                Total Change Orders: {changeOrders.length}
              </span>
              <span className="font-mono font-bold text-lg">
                {formatCurrency(total)}
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function BillingMonitoringTab({ user, initialProjectId }) {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(initialProjectId || '');
  const [billing, setBilling] = useState(null);
  const [changeOrders, setChangeOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [constructionProgress, setConstructionProgress] = useState(null);
  
  useEffect(() => {
    loadProjects();
  }, []);
  
  // Auto-load billing when initialProjectId is provided
  useEffect(() => {
    if (initialProjectId && projects.length > 0) {
      setSelectedProjectId(initialProjectId);
      loadBilling(initialProjectId);
    }
  }, [initialProjectId, projects]);
  
  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const response = await axios.get(`${API_URL}/api/billing/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load projects: ${errorMsg}`);
    } finally {
      setLoadingProjects(false);
    }
  };
  
  const loadBilling = async (projectId) => {
    if (!projectId) {
      setBilling(null);
      setChangeOrders([]);
      setConstructionProgress(null);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/billing/${projectId}`);
      setBilling(response.data);
      loadConstructionProgress(response.data?.project_name);
    } catch (error) {
      console.error('Failed to load billing:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
    loadChangeOrders(projectId);
  };

  const loadConstructionProgress = async (projectName) => {
    if (!projectName) { setConstructionProgress(null); return; }
    try {
      const g = await axios.get(`${API_URL}/api/gantt`);
      const charts = (g.data || []).filter(c => c.project_name === projectName);
      const chart = charts.find(c => (c.department || '').toLowerCase() === 'construction') || charts[0];
      setConstructionProgress(chart ? (chart.project_progress ?? null) : null);
    } catch {
      setConstructionProgress(null);
    }
  };

  const loadChangeOrders = async (projectId) => {
    if (!projectId) {
      setChangeOrders([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/change-orders?project_id=${projectId}`);
      setChangeOrders(response.data || []);
    } catch (error) {
      console.error('Failed to load change orders:', error);
    }
  };

  const handleCOBillingUpdate = async (coId, patch) => {
    // Optimistic update
    setChangeOrders(prev => prev.map(co => co.id === coId ? { ...co, ...patch } : co));
    try {
      await axios.put(`${API_URL}/api/change-orders/${coId}/billing`, patch);
    } catch (error) {
      console.error('Failed to update change order billing:', error);
      toast.error('Failed to update change order billing');
      loadChangeOrders(selectedProjectId);
    }
  };
  
  const handleProjectChange = (projectId) => {
    setSelectedProjectId(projectId);
    loadBilling(projectId);
  };
  
  const saveBilling = async (updatedBilling, showToast = false) => {
    setSaving(true);
    try {
      const resp = await axios.post(`${API_URL}/api/billing/${selectedProjectId}`, updatedBilling);
      // Merge any newly auto-generated contract codes into local state
      const patch = {};
      if (resp.data?.design_contract_code) patch.design_contract_code = resp.data.design_contract_code;
      if (resp.data?.construction_contract_code) patch.construction_contract_code = resp.data.construction_contract_code;
      if (Object.keys(patch).length > 0) {
        setBilling(prev => (prev ? { ...prev, ...patch } : prev));
      }
      if (showToast) {
        toast.success('Billing saved');
      }
      // Don't reload - we already have the updated data locally
    } catch (error) {
      console.error('Failed to save billing:', error);
      toast.error('Failed to save billing');
    } finally {
      setSaving(false);
    }
  };
  
  // Debounce timer ref
  const saveTimeoutRef = useRef(null);
  
  // Debounced save for contract prices (saves after user stops typing)
  const debouncedSave = useCallback((updatedBilling) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveBilling(updatedBilling, false);
    }, 800); // Save after 800ms of no typing
  }, [selectedProjectId]);
  
  const handleDesignPriceChange = (price) => {
    const updated = { ...billing, design_contract_price: price };
    setBilling(updated);
    debouncedSave(updated);
  };
  
  const handleConstructionPriceChange = (price) => {
    const updated = { ...billing, construction_contract_price: price };
    setBilling(updated);
    debouncedSave(updated);
  };

  const handleCategoryFieldChange = (category, field, value) => {
    const updated = { ...billing, [`${category}_${field}`]: value };
    setBilling(updated);
    debouncedSave(updated);
  };
  
  const handlePhaseUpdate = async (category, phaseId, data) => {
    const field = `${category}_phases`;
    const phases = billing[field].map(p => 
      p.id === phaseId ? { ...p, ...data } : p
    );
    const updated = { ...billing, [field]: phases };
    setBilling(updated);
    debouncedSave(updated);
  };
  
  const handlePhaseAdd = async (category, name, percentage) => {
    const field = `${category}_phases`;
    const newPhase = {
      id: crypto.randomUUID(),
      name,
      percentage,
      status: "For Billing",
      paid_date: null,
      is_urgent: false
    };
    const phases = [...billing[field], newPhase];
    const updated = { ...billing, [field]: phases };
    setBilling(updated);
    await saveBilling(updated, true);
  };
  
  const handlePhaseDelete = async (category, phaseId) => {
    if (!window.confirm('Are you sure you want to delete this phase?')) return;
    
    const field = `${category}_phases`;
    const phases = billing[field].filter(p => p.id !== phaseId);
    const updated = { ...billing, [field]: phases };
    setBilling(updated);
    await saveBilling(updated, true);
  };
  
  return (
    <div className="space-y-6">
      {/* Project Selector */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <Label className="text-base font-medium whitespace-nowrap">Select Project:</Label>
            <Select value={selectedProjectId} onValueChange={handleProjectChange} disabled={loadingProjects}>
              <SelectTrigger className="w-full max-w-md" data-testid="project-selector">
                <SelectValue placeholder={loadingProjects ? "Loading projects..." : "Choose a project to view billing"} />
              </SelectTrigger>
              <SelectContent>
                {projects.length === 0 && !loadingProjects ? (
                  <SelectItem value="no-projects" disabled>No projects found</SelectItem>
                ) : (
                  <GroupedProjectItems
                    projects={projects}
                    valueKey="project_id"
                    getLabel={(project) => `${project.project_name}${project.project_code ? ` (${project.project_code})` : ''}`}
                  />
                )}
              </SelectContent>
            </Select>
            {loadingProjects && <span className="text-sm text-muted-foreground animate-pulse">Loading...</span>}
          </div>
        </CardContent>
      </Card>
      
      {/* Billing Content */}
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="animate-pulse">Loading billing data...</div>
          </CardContent>
        </Card>
      ) : billing ? (
        <div className="space-y-6">
          {/* Project Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">{billing.project_name}</h2>
            {saving && <span className="text-sm text-muted-foreground animate-pulse">Saving...</span>}
          </div>
          
          {/* Design Category */}
          <BillingCategory
            title="Architectural / Design"
            category="design"
            phases={billing.design_phases || []}
            contractPrice={billing.design_contract_price || 0}
            contractCode={billing.design_contract_code}
            startDate={billing.design_start_date}
            targetCompletion={billing.design_target_completion}
            approvedBudget={billing.design_approved_budget}
            status={billing.design_status}
            onFieldChange={(field, value) => handleCategoryFieldChange('design', field, value)}
            onPriceChange={handleDesignPriceChange}
            onPhaseUpdate={handlePhaseUpdate}
            onPhaseAdd={handlePhaseAdd}
            onPhaseDelete={handlePhaseDelete}
          />
          
          {/* Construction Category */}
          <BillingCategory
            title="Construction"
            category="construction"
            scheduleProgress={constructionProgress}
            phases={billing.construction_phases || []}
            contractPrice={billing.construction_contract_price || 0}
            contractCode={billing.construction_contract_code}
            startDate={billing.construction_start_date}
            targetCompletion={billing.construction_target_completion}
            approvedBudget={billing.construction_approved_budget}
            status={billing.construction_status}
            onFieldChange={(field, value) => handleCategoryFieldChange('construction', field, value)}
            onPriceChange={handleConstructionPriceChange}
            onPhaseUpdate={handlePhaseUpdate}
            onPhaseAdd={handlePhaseAdd}
            onPhaseDelete={handlePhaseDelete}
          />

          {/* Change Orders Category */}
          <ChangeOrderBillingCategory
            changeOrders={changeOrders}
            onUpdate={handleCOBillingUpdate}
          />
        </div>
      ) : selectedProjectId ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No billing data found for this project
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Select a project from the dropdown above to view and manage billing
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function AccountingPage({ user, onLogout, theme, onThemeToggle }) {
  const location = useLocation();
  const initialProjectId = location.state?.selectedProjectId;
  
  // Only Principal Architect can access this page
  if (user.role !== 'principal') {
    return (
      <Layout user={user} onLogout={onLogout} theme={theme} onThemeToggle={onThemeToggle}>
        <div className="p-6 md:p-8">
          <Card>
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
              <h2 className="text-xl font-bold mb-2">Access Denied</h2>
              <p className="text-muted-foreground">Only Principal Architect can access the Finance portal.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 md:p-8">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
            <Receipt className="w-8 h-8 text-primary" />
            Finance
          </h1>
          <p className="text-muted-foreground mt-1">Manage project billing and financial tracking</p>
        </div>
        
        <Tabs scope="finance" defaultValue="billing" className="space-y-6">
          <TabsList className="flex-wrap h-auto gap-1 justify-start w-full sm:w-auto">
            <TabsTrigger value="billing" data-testid="tab-billing-monitoring">
              <Receipt className="w-4 h-4 mr-2" />
              Billing Monitoring
            </TabsTrigger>
            <TabsTrigger value="petty-cash" data-testid="tab-petty-cash-finance">
              <Wallet className="w-4 h-4 mr-2" />
              Petty Cash Monitoring
            </TabsTrigger>
            <TabsTrigger value="change-orders" data-testid="tab-change-orders-finance">
              <ClipboardList className="w-4 h-4 mr-2" />
              Change Orders
            </TabsTrigger>
            <TabsTrigger value="project-bom" data-testid="tab-project-bom-finance">
              <Layers className="w-4 h-4 mr-2" />
              Project BOM
            </TabsTrigger>
            <TabsTrigger value="accounting-database" data-testid="tab-accounting-database-finance">
              <Database className="w-4 h-4 mr-2" />
              Accounting Database
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="billing">
            <BillingMonitoringTab user={user} initialProjectId={initialProjectId} />
          </TabsContent>

          <TabsContent value="petty-cash">
            <PettyCashAdminTab />
          </TabsContent>

          <TabsContent value="change-orders">
            <ChangeOrdersTab mode="finance" />
          </TabsContent>

          <TabsContent value="project-bom">
            <ProjectBOMTab />
          </TabsContent>

          <TabsContent value="accounting-database">
            <AccountingDatabaseTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
